import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';

import { CurrencyOption, CurrencyService } from '../../../core/services/currency.service';

@Component({
  selector: 'app-currency-select',
  standalone: false,
  templateUrl: './currency-select.html',
  styleUrl: './currency-select.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencySelect),
      multi: true,
    },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => CurrencySelect), multi: true },
  ],
})
export class CurrencySelect implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  @Input() placeholder = 'Select currency';
  @Input() defaultToBase = false;
  @Input() selectClass = 'form-select';
  @Input() disabled = false;

  options: CurrencyOption[] = [];
  value = '';
  loadingDefault = false;
  defaultError = '';
  private initialized = false;
  private destroyed = false;
  private onValidatorChange: () => void = () => undefined;

  readonly search = (query: string) => this.loadOptions(query);
  readonly preload = (values: string[]) =>
    this.currencyService.preload(values);

  private internalDisabled = false;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private readonly currencyService: CurrencyService, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initialized = true;
    void this.loadDefault();
  }

  ngOnDestroy(): void { this.destroyed = true; }

  async loadDefault(): Promise<void> {
    if (!this.defaultToBase || this.value || this.loadingDefault || this.destroyed) return;
    this.loadingDefault = true;
    this.defaultError = '';
    this.onValidatorChange();
    try {
      const code = await this.currencyService.organisationDefaultCode();
      if (!this.destroyed && !this.value) {
        this.value = code;
        this.onChange(code);
      }
    } catch {
      if (!this.destroyed && !this.value) this.defaultError = 'Unable to load the organisation currency. Retry or select an enabled currency.';
    } finally {
      this.loadingDefault = false;
      if (!this.destroyed) {
        this.onValidatorChange();
        this.cdr.detectChanges();
      }
    }
  }

  validate(): ValidationErrors | null {
    return this.defaultToBase && !this.value && (this.loadingDefault || this.defaultError)
      ? { currencyDefault: this.defaultError || 'Loading organisation currency.' } : null;
  }

  registerOnValidatorChange(fn: () => void): void { this.onValidatorChange = fn; }

  get isDisabled(): boolean {
    return this.disabled || this.internalDisabled;
  }

  writeValue(value: unknown): void {
    this.value = typeof value === 'string' ? value : '';
    if (this.initialized && !this.value) void Promise.resolve().then(() => this.loadDefault());
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.internalDisabled = disabled;
  }

  valueChanged(value: string | string[]): void {
    this.value = Array.isArray(value) ? String(value[0] ?? '') : value;
    this.onChange(this.value);
    this.onTouched();
    this.defaultError = '';
    this.onValidatorChange();
  }

  private async loadOptions(query: string): Promise<CurrencyOption[]> {
    this.options = await this.currencyService.options(query);
    return this.options;
  }
}
